import Cookie from '@/lib/cookie';
import Analytics from '@/lib/analytics';
import {HTTPError} from '@/lib/fetch/base';
import {getProfile} from '@/lib/fetch/profile';
import {LocalSetting} from '@/hooks/local-storage';
import {AuthenticationStatus} from '@/types';
import type {UserData} from '@/hooks/user-data';

/**
 * Reads the application's settings from the cookie that is received from the client.
 * @param decrypt - Decrypt the cookie value from the `Base64` format.
 */
export async function getSetting(decrypt = false) {
  const setting = await new Cookie().get('setting');
  if (setting) {
    return JSON.parse(
      decrypt ? Buffer.from(setting, 'base64').toString('utf-8') : setting
    ) as LocalSetting;
  }
}

/**
 * Returns the user's authentication token.
 *
 * The token could be a cookie that is received from
 * the client or a data that is stored in the browser.
 *
 * It's not possible to use cookies in the build time.
 * Reading cookies should not be placed in try-catch block.
 * @see https://nextjs.org/docs/messages/ppr-caught-error
 */
export async function getUserToken() {
  return process.env.NEXT_PHASE !== 'phase-production-build'
    ? await new Cookie().get('token')
    : undefined;
}

/**
 * Gets the user's profile info based on whether
 * the client has sent the authentication token as cookie.
 *
 * @param throwError - Throw the error instead of returning it.
 * The error types other than {@link HTTPError} always will be thrown.
 *
 * @returns The user's profile info.
 * The `null` will be returned whenever the {@link throwError} parameter
 * is not `true` and no authentication token is received or the
 * token is not valid. For other types of errors, the error object
 * will be returned instead.
 */
export async function getUserData(
  throwError: false
): Promise<UserData | null | HTTPError>;
export async function getUserData(throwError: true): Promise<UserData>;
export async function getUserData(
  throwError?: boolean
): Promise<UserData | null | HTTPError>;
export async function getUserData(throwError?: boolean) {
  if (await getUserToken())
    try {
      return {profile: await getProfile()} as UserData;
    } catch (error) {
      if (error instanceof HTTPError) {
        if (error.code === 401 && !throwError)
          // Token is not accepted
          return null;

        // Sending an event for unhandled exceptions
        new Analytics().exceptionEvent(
          Analytics.serializeErrorMessage(error.message, error),
          true
        );

        if (throwError || process.env.NODE_ENV === 'development')
          // If the error is rethrow in the production environment,
          // the `global-error.ts` file is needed to be defined to
          // handle the errors in the `RootLayout()` component from
          // the `layout.ts` global file because this function is
          // called there.
          // Also, anywhere this function is needed to be used,
          // errors would prevent the entire page to render and
          // the nearest `error.ts` file will be rendered as fallback.
          throw error;

        return error;
      }

      throw error;
    }
  else {
    return null;
  }
}

/** Returns the client's authentication status. */
export function getAuthenticationStatus(
  userData?: Awaited<ReturnType<typeof getUserData>>
) {
  if ((userData as UserData)?.profile) {
    return AuthenticationStatus.Authorized;
  } else if (userData === null) {
    return AuthenticationStatus.Unauthorized;
  } else {
    return AuthenticationStatus.Unknown;
  }
}

/** Returns whether the client is authenticated. */
export async function isAuthenticated(
  userData?: Awaited<ReturnType<typeof getUserData>>
) {
  return (
    getAuthenticationStatus(
      userData !== undefined ? userData : await getUserData()
    ) === AuthenticationStatus.Authorized
  );
}
